import { useEffect, useState } from 'react';

interface ConfettiEffectProps {
  trigger: boolean;
  type?: 'complete' | 'skip' | 'missed';
  origin?: { x: number; y: number };
  onComplete?: () => void;
}

const ConfettiEffect = ({ trigger, type = 'complete', origin = { x: 50, y: 50 }, onComplete }: ConfettiEffectProps) => {
  const [particles, setParticles] = useState<Array<{ 
    id: number; 
    x: number; 
    y: number; 
    emoji: string;
    color: string; 
    delay: number;
    rotation: number;
    scale: number;
    velocityX: number;
    velocityY: number;
    rotationSpeed: number;
    animationType: 'explode' | 'float' | 'bounce';
  }>>([]);

  const getEmojisForType = (confettiType: 'complete' | 'skip' | 'missed') => {
    switch (confettiType) {
      case 'complete':
        return ['🎉', '🥳', '🎊', '🔥', '⭐', '💫', '✨', '🌟', '🏆', '💪', '👏', '🙌', '❤️', '💯', '🚀', '😊', '😄', '🤩'];
      case 'skip':
        return ['💭', '😌', '🤷', '💙', '🌸', '🌿', '☁️', '💆', '🧘', '😇', '💛', '🤗', '🌅', '🕊️', '🌱'];
      case 'missed':
        return ['😔', '💔', '😞', '😢', '🥺', '😟', '😕', '💧', '🌧️', '😪', '😰', '😓', '🤧', '😷', '🫤'];
      default:
        return ['🎉', '🥳', '🎊'];
    }
  };

  const getColorsForType = (confettiType: 'complete' | 'skip' | 'missed') => {
    switch (confettiType) {
      case 'complete':
        return ['#22C55E', '#16A34A', '#15803D', '#F59E0B', '#EAB308', '#F97316', '#DC2626'];
      case 'skip':
        return ['#3B82F6', '#60A5FA', '#93C5FD', '#FBBF24', '#FDE047', '#A3A3A3', '#D1D5DB'];
      case 'missed':
        return ['#6B7280', '#9CA3AF', '#D1D5DB', '#EF4444', '#F87171', '#FCA5A5', '#374151'];
      default:
        return ['#F58026', '#133C7D'];
    }
  };

  useEffect(() => {
    if (trigger) {
      const emojis = getEmojisForType(type);
      const colors = getColorsForType(type);
      
      const particleCount = type === 'complete' ? 50 : type === 'skip' ? 30 : 20;
      
      const newParticles = Array.from({ length: particleCount }, (_, i) => {
        const angle = (Math.PI * 2 * i) / particleCount;
        const velocity = type === 'complete' ? 3 + Math.random() * 4 : 2 + Math.random() * 3;
        
        return {
          id: i,
          x: origin.x,
          y: origin.y,
          emoji: emojis[Math.floor(Math.random() * emojis.length)],
          color: colors[Math.floor(Math.random() * colors.length)],
          delay: Math.random() * 0.2,
          rotation: Math.random() * 360,
          scale: 0.8 + Math.random() * 0.8,
          velocityX: Math.cos(angle) * velocity + (Math.random() - 0.5) * 2,
          velocityY: Math.sin(angle) * velocity + (Math.random() - 0.5) * 2,
          rotationSpeed: (Math.random() - 0.5) * 1080,
          animationType: (['explode', 'float', 'bounce'][Math.floor(Math.random() * 3)]) as 'explode' | 'float' | 'bounce',
        };
      });
      
      setParticles(newParticles);
      
      const timer = setTimeout(() => {
        setParticles([]);
        onComplete?.();
      }, type === 'complete' ? 2000 : 1500);
      
      return () => clearTimeout(timer);
    }
  }, [trigger, type, origin.x, origin.y, onComplete]);

  if (particles.length === 0) return null;

  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes confetti-explode {
            0% {
              transform: translate(0, 0) scale(0) rotate(0deg);
              opacity: 1;
            }
            20% {
              transform: translate(var(--dx), var(--dy)) scale(1.2) rotate(180deg);
              opacity: 1;
            }
            100% {
              transform: translate(calc(var(--dx) * 2), calc(var(--dy) * 2 + 100vh)) scale(0.3) rotate(720deg);
              opacity: 0;
            }
          }
          
          @keyframes confetti-float {
            0% {
              transform: translate(0, 0) scale(0) rotate(0deg);
              opacity: 1;
            }
            25% {
              transform: translate(var(--dx), var(--dy)) scale(1) rotate(90deg);
              opacity: 1;
            }
            50% {
              transform: translate(calc(var(--dx) * 1.5), calc(var(--dy) * 1.5)) scale(1.1) rotate(270deg);
              opacity: 1;
            }
            100% {
              transform: translate(calc(var(--dx) * 2), calc(var(--dy) * 2 + 80vh)) scale(0.2) rotate(540deg);
              opacity: 0;
            }
          }
          
          @keyframes confetti-bounce {
            0% {
              transform: translate(0, 0) scale(0) rotate(0deg);
              opacity: 1;
            }
            30% {
              transform: translate(calc(var(--dx) * 0.7), calc(var(--dy) * 0.7)) scale(1.3) rotate(180deg);
              opacity: 1;
            }
            60% {
              transform: translate(calc(var(--dx) * 1.2), calc(var(--dy) * 1.2 + 20px)) scale(0.9) rotate(360deg);
              opacity: 1;
            }
            100% {
              transform: translate(calc(var(--dx) * 2), calc(var(--dy) * 2 + 100vh)) scale(0.1) rotate(720deg);
              opacity: 0;
            }
          }
          
          .confetti-particle {
            position: absolute;
            font-size: 20px;
            font-weight: bold;
            pointer-events: none;
            user-select: none;
          }
          
          .confetti-explode {
            animation: confetti-explode 1.5s ease-out forwards;
          }
          
          .confetti-float {
            animation: confetti-float 1.8s ease-in-out forwards;
          }
          
          .confetti-bounce {
            animation: confetti-bounce 1.4s cubic-bezier(0.68, -0.55, 0.265, 1.55) forwards;
          }
        `
      }} />
      {particles.map((particle) => (
        <div
          key={particle.id}
          className={`confetti-particle confetti-${particle.animationType}`}
          style={{
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            color: particle.color,
            fontSize: `${16 * particle.scale}px`,
            animationDelay: `${particle.delay}s`,
            '--dx': `${particle.velocityX * 50}px`,
            '--dy': `${particle.velocityY * 50}px`,
          } as React.CSSProperties}
        >
          {particle.emoji}
        </div>
      ))}
    </div>
  );
};

export default ConfettiEffect;
